import * as React from 'react';

import {
  QueryBuilderCopy,
  QueryBuilderTestIds,
} from 'bones/components/molecules/QueryBuilder/types/schema';
import { processTestIds } from 'bones/utils/processTestIds';
import {
  DatatypeOperatorTypeValue,
  DatatypeRange,
} from 'bones/utils/queryBuilder/constants';
import { SelectItem } from 'bones/utils/select/types';

export interface QueryBuilderColors {
  darkIcon: string;
  anyClassColorId: string;
}

export const defaultColors: QueryBuilderColors = {
  darkIcon: '#003e51',
  anyClassColorId: 'sd-qb-gray',
};

const defaultCopy: QueryBuilderCopy = {
  body: {
    expression: {
      duplicate: 'Duplicate',
      remove: 'Remove',
    },
    conjunction: {
      andNot: 'AND NOT',
      or: 'OR',
    },
    class: {
      anyLabel: 'Any Class',
      placeholder: 'Select class...',
      existingInstance: 'Existing instance',
      newInstance: 'New instance',
    },
    input: {
      booleanFalse: 'False',
      booleanTrue: 'True',
      datePlaceholder: 'Select date',
      dateRangePlaceholder: 'Select range',
      numberPlaceholder: 'Enter number...',
    },
    property: {
      attributes: 'Attributes',
      classify: 'Classify As',
      instance: 'In',
      label: 'Label',
      relationships: 'Relationships',
      placeholder: 'Select property...',
    },
    instance: {
      createNew: 'Use ',
      valueTooltip:
        'To specify an instance, select from the suggested options or provide a set of valid IRI enclosed in backticks ([`]) and comma-separated ([,])',
    },
    operator: {
      comparison: 'Comparison',
      placeholder: 'Set value',
    },
  },
  error: {
    classInstanceDefined:
      'Class instance is already defined. To continue with this search, select a different class instance or property.',
    classModelDomainOrRange:
      'Class instances must be associated to the selected property. To continue with this search, select a valid class instance.',
    classModelUndefined:
      'Class instances must be defined in the model. To continue with this search, select an existing class instance.',
    classPreviousInstance:
      'Class instances must be a previous instance. To continue with this search, select a previous class instance.',
    classSubjectConnected:
      'Class instances must be associated with previous instances. To continue with this search, connect this class instance to a previous instance.',
    classVariableInvalid:
      'Class instances cannot be associated to instances in other AND NOT or OR expressions. To continue with this search, select a different class instance.',
    instanceValueBackticks: 'Valid IRIs must be enclosed in backticks ([`]).',
    instanceValueCharacters: (chars: string[]) =>
      `The IRI is invalid. "${chars.join(',')}" is not allowed.`,
    instanceValueSemicolon: 'The IRI must include a semicolon ([:]).',
    instanceValueSplitCharacter: 'Valid IRIs must be comma separated ([,]).',
    instanceValueStartsWith:
      'The IRI must start with an alphabet character ([A-Za-z]).',
    propertyModelUndefined:
      'Properties must be defined in the model. To continue with this search, select an existing property.',
    propertyModelDomainOrRange:
      'Properties must be associated with the selected class. To continue with this search, select a valid property.',
  },
};

const defaultStringTestIds: Omit<
  QueryBuilderTestIds,
  | 'classSelectorItem'
  | 'classSelectorSubItem'
  | 'instanceSelectorItem'
  | 'operatorSelectorItem'
  | 'operatorSelectorSubItem'
  | 'propertySelectorItem'
  | 'propertySelectorSubItem'
> = {
  addExpressionButton: '',
  addExpressionCascadeButton: '',
  addSubExpressionButton: '',
  comparisonClassSelector: '',
  comparisonPropertySelector: '',
  container: '',
  datatype: {
    booleanValue: '',
    booleanValueInput: '',
    dateRangeValue: '',
    dateValue: '',
    numericValue: '',
    numericValueInput: '',
    stringValue: '',
    stringValueInput: '',
    timeRangeValue: '',
    timeValue: '',
    value: '',
  },
  domainClassSelector: '',
  duplicateExpressionButton: '',
  error: '',
  expressionCascade: '',
  expressionContainer: '',
  filterConnector: '',
  filterContainer: '',
  fullGraphToggle: '',
  instanceSelector: '',
  instanceSelectorInput: '',
  instanceSelectorNewItem: '',
  instanceSelectorTooltipTarget: '',
  operatorSelector: '',
  propertySelector: '',
  rangeClassSelector: '',
  removeExpressionButton: '',
  removeExpressionCascadeButton: '',
  resetClearButton: '',
  subExpressionBlock: '',
  submitButton: '',
  topExpressionBlock: '',
};
processTestIds(defaultStringTestIds, '-', 'sd-query-builder');

const defaultTestIds: QueryBuilderTestIds = {
  ...defaultStringTestIds,
  classSelectorItem: (id: string) => `sd-query-builder-class-item-${id}`,
  classSelectorSubItem: (id: string, subId: string) =>
    `sd-query-builder-class-item-${id}-sub-${subId}`,
  instanceSelectorItem: (id: string) => `sd-query-builder-instance-item-${id}`,
  operatorSelectorItem: (id: string) => `sd-query-builder-operator-item-${id}`,
  operatorSelectorSubItem: (id: string, subId: string) =>
    `sd-query-builder-operator-item-${id}-sub-${subId}`,
  propertySelectorItem: (id: string) => `sd-query-builder-property-item-${id}`,
  propertySelectorSubItem: (id: string, subId: string) =>
    `sd-query-builder-property-item-${id}-sub-${subId}`,
};

export interface DatatypeValueProps {
  invalid?: boolean;
  onSetValue: (value: string) => void;
  operatorType: DatatypeOperatorTypeValue;
  predicate: string;
  range?: DatatypeRange;
  subject: string;
  value: string | null;
}

export interface QueryBuilderSchema {
  colors: QueryBuilderColors;
  copy: QueryBuilderCopy;
  testIds: QueryBuilderTestIds;

  andNotDisabled?: boolean;
  comparisonDisabled?: boolean;
  datatypeValueInput?: {
    [rangeIri: string]: React.VFC<DatatypeValueProps>;
  };
  duplicateDisabled?: boolean;
  hiddenOperators?: Set<DatatypeOperatorTypeValue>;
  instanceDisabled?: boolean;
  limitOperators?: boolean;
  orDisabled?: boolean;
  useInstanceLabels?: (iris: string[]) => {
    labelsByIri: Record<string, string>;
    isLoading: boolean;
  };
  useInstanceSuggestions?: (
    classIri: string,
    query: string
  ) => { suggestions: SelectItem[]; isLoading: boolean };
  validateModel?: boolean;
}

export const DEFAULT_QUERY_BUILDER_SCHEMA: QueryBuilderSchema = {
  colors: defaultColors,
  copy: defaultCopy,
  testIds: defaultTestIds,

  andNotDisabled: false,
  duplicateDisabled: false,
  instanceDisabled: false,
  limitOperators: false,
  orDisabled: false,
  validateModel: false,
};

export const QueryBuilderSchemaContext =
  React.createContext<QueryBuilderSchema>(DEFAULT_QUERY_BUILDER_SCHEMA);

export const useQueryBuilderSchemaContext = () => {
  const schema = React.useContext(QueryBuilderSchemaContext);
  return schema;
};
